import Parse from 'parse';

const MachineDiaryModule = {
  namespaced: true,
  state: {
    machines: null,
    vehicles: null,
    entries: [],
    currentMachine: null,
    rolesAndUsers: {},
    verwalters: [],
    chosenEmployee: null,
  },
  mutations: {
    setMachines(state, machines) {
      state.machines = machines;
    },
    setVehicles(state, vehicles) {
      state.vehicles = vehicles;
    },
    setCurrentMachine(state, machine) {
      state.currentMachine = machine;
    },
    setRoleAndUsers(state, { name, users }) {
      state.rolesAndUsers[name] = users;
    },
    setVerwalters(state, verwalters) {
      state.verwalters = verwalters;
    },
    setEntry(state, newEntry) {
      const newEntries = [];
      for (const entry of state.entries) {
        if (entry.id === newEntry.id) newEntries.push(newEntry);
        else newEntries.push(entry);
      }
      state.entries = newEntries;
    },
    setChosenEmployee(state, payload) {
      state.chosenEmployee = payload;
    },
    addAssignedEntries(state, entries) {
      state.entries = [...state.entries, ...entries];
    },
    addDoneEntries(state, entries) {
      state.entries = [...state.entries, ...entries];
    },
    addEntry(state, entry) {
      state.entries = [...state.entries, entry];
    },
    addOpenEntries(state, entries) {
      state.entries = [...state.entries, ...entries];
    },
  },
  actions: {
    async loadAllMachines({ commit }) {
      const query = new Parse.Query('Inventory');
      query.limit(3000);
      const result = await query.find();
      commit(
        'setMachines',
        result.filter((el) => el.get('machineryTypeDescription') !== 'Fuhrpark')
      );
      commit(
        'setVehicles',
        result.filter((el) => el.get('machineryTypeDescription') === 'Fuhrpark')
      );
    },
    async loadRolesAndUsers({ commit }) {
      try {
        const machines = new Parse.Query('_Role');
        machines.limit(3000);
        machines.startsWith('name', 'Maschinen-');
        const vehicles = new Parse.Query('_Role');
        vehicles.limit(3000);
        vehicles.startsWith('name', 'Fuhrpark-');
        const query = Parse.Query.or(machines, vehicles);
        query.limit(3000);
        const result = await query.find();
        for (const role of result) {
          const users = await role.relation('users').query().find();
          commit('setRoleAndUsers', { name: role.get('name'), users: users || [] });
        }
      } catch (err) {
        console.log(err);
      }
    },
    async loadVerwalters({ commit }) {
      const query = new Parse.Query('_Role');
      query.equalTo('name', 'Verwalter');
      query.include('users');
      try {
        const result = await query.find();
        const users = await result[0].relation('users').query().find();
        commit('setVerwalters', users);
      } catch (err) {
        console.log(err);
      }
    },
    async modifyEntry({ commit, dispatch }, { entry, params }) {
      commit('setEntry', entry);
      await entry.save();
      if (params) await dispatch('notifyUser', params);
      else
        await dispatch('notifyVerwalters', {
          reason: 'entryFinalized',
          suffix: entry.get('state') + ' : ' + entry.get('machine').get('description'),
        });
    },
    // eslint-disable-next-line no-empty-pattern
    async notifyUser({}, params) {
      await Parse.Cloud.run('sendPushNotification', params, {});
    },
    async notifyVerwalters({ state }, { reason, suffix }) {
      let message;
      switch (reason) {
        case 'entryFinalized':
          message = 'Eintrag im Gerätetagebuch fertiggestellt!';
          break;
        case 'newEntry':
          message = 'Neuer Eintrag im Gerätetagebuch!';
          break;
      }

      for (const verwalter of state.verwalters)
        await Parse.Cloud.run(
          'sendPushNotification',
          {
            userToId: verwalter.id,
            not: message + ' ' + suffix,
            uriSuffix: 'machine-diary/',
            type: 'machine-diary',
          },
          {}
        );
    },
    chooseEmployee({ commit }, payload) {
      commit('setChosenEmployee', payload);
    },
    async loadOpenEntries({ state, commit }) {
      if (!state.entries.filter((en) => !en.get('resolved') && !en.get('user')).length) {
        const query = new Parse.Query('MachineDiary');
        query.limit(3000);
        query.include('author');
        query.include('machine');
        query.include('user');
        query.equalTo('resolved', false);
        query.equalTo('user', null);
        try {
          const res = await query.find();
          commit('addOpenEntries', res);
        } catch (err) {
          console.log(err);
        }
      }
    },
    async loadEntriesAssignedToMe({ commit }) {
      const query = new Parse.Query('MachineDiary');
      query.equalTo('user', Parse.User.current());
      query.notEqualTo('resolved', true);
      query.include('machine');
      try {
        const res = await query.find();
        commit('addAssignedEntries', res);
      } catch (err) {
        console.log(err);
      }
    },
    async loadAssignedEntries({ state, commit }) {
      const assignedQuery = new Parse.Query('MachineDiary');
      assignedQuery.limit(3000);
      assignedQuery.include('author');
      assignedQuery.include('machine');
      assignedQuery.include('user');
      assignedQuery.notEqualTo('user', null);
      assignedQuery.equalTo('resolved', false);
      if (state.entries.filter((en) => !en.get('resolved') && en.get('user')?.id === Parse.User.current().id).length)
        assignedQuery.notEqualTo('user', Parse.User.current());
      try {
        const res = await assignedQuery.find();
        commit('addAssignedEntries', res);
      } catch (err) {
        console.log(err);
      }
    },
    async loadDoneEntries({ commit }) {
      const doneQuery = new Parse.Query('MachineDiary');
      doneQuery.limit(3000);
      doneQuery.include('author');
      doneQuery.include('machine');
      doneQuery.include('user');
      doneQuery.equalTo('resolved', true);
      try {
        const res = await doneQuery.find();
        commit('addDoneEntries', res);
      } catch (err) {
        console.log(err);
      }
    },
    addNewEntry({ commit }, entry) {
      commit('addEntry', entry);
    },
  },
  getters: {
    machines: (state) => state.machines,
    vehicles: (state) => state.vehicles,
    entries: (state) => state.entries,
    currentMachine: (state) => state.currentMachine,
    rolesAndUsers: (state) => state.rolesAndUsers,
    chosenEmployee: (state) => state.chosenEmployee,
    openEntriesCount: (state) => state.entries.filter((en) => !en.get('resolved') && !en.get('user')).length,
    entriesAssignedToMeCount: (state) =>
      state.entries.filter((en) => !en.get('resolved') && en.get('user').id === Parse.User.current().id).length,
  },
};
export default MachineDiaryModule;
